import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClubTheme } from '../contexts/ClubThemeContext';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Divider,
  Collapse,
  TextField,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  SportsBasketball
} from '@mui/icons-material';

import SelectSelectionNational from '../components/SelectSelectionNational'; 
import NationalitiesSelect from '../components/NationalitiesSelect';
import NationalitiesSelect2 from '../components/NationalitiesSelect2';

// Clés de traduction (seront traduites dans le composant)
const offensiveRoleKeys = [
  'ballHandler', 'secondaryBallHandler', 'dominantScorer', 'floorGeneral', 'shootingSpecialist',
  'creatorWing', 'versatileWing', 'strongWing', 'slasherWing', 'spotUpWing', 'threeAndD',
  'traditionalPowerForward', 'skilledForward', 'stretchFour',
  'creatorCenter', 'versatileCenter', 'midRangeCenter', 'stretchFive', 'postScorer', 'rimFinisher', 'pickAndRollBig', 'marginalOffensiveRole'
];

const defensiveRoleKeys = [
  'intenseOneOnOne', 'perimeterDefender', 'wingStopper', 'switchDefender',
  'anchorBig', 'defensiveRebounder', 'rimProtector', 'pointOfAttack', 'slowLaterally', 'nonDefender'
];

const JEAttributeKeys = [
  'oneOnOne', 'threeLevelScorer', 'offBallActivity', 'athletic',
  'pickAndRollAbility', 'foulDrawing', 'clutch', 'tough', 'energy',
  'wingspan', 'physical', 'hustle', 'bbiq', 'postGame', 'paintTouches',
  'slasher', 'scoring', 'firstStep', 'consistentProduction', 'rebounding',
  'talent', 'shooter', 'catchAndShoot', 'cornerThree', 'pullUpShooting',
  'transition', 'blueCollar', 'speedAgility', 'versatility', 'verticality'
];

const JIAttributeKeys = [
  'oneOnOne', 'offBallActivity', 'athletic', 'screenAndRoll',
  'effectiveScreens', 'softTouch', 'clutch', 'tough', 'energy',
  'wingspan', 'footwork', 'physical', 'hustle', 'bbiq', 'postUpFaceUp',
  'lobThreat', 'scoring', 'consistentProduction', 'rimProtection',
  'rebounding', 'rimRunner', 'talent', 'shooter', 'catchAndShoot',
  'cornerThreePopOut', 'pullUpShooting', 'blueCollar', 'transition',
  'multipleEfforts', 'speedAgility', 'versatility', 'verticality'
];

const vbcRatingKeys = [
  'lowLevelProspect', 'midLevelProspect', 'goodLevelProspect', 'topProspect', 'futureStar',
  'ebaLow', 'ebaHigh', 'lebLow', 'lebHigh', 'acbRotation',
  'bottomBclEurocup', 'midBclEurocup', 'topBclEurocup', 'elMid', 'elTop',
  'nbaRotation', 'nbaAllStar', 'collegeLow', 'collegeMid', 'collegeTop'
];

const draftPositions = [
  ...Array(30).fill(null).map((_, i) => `1st Round, #${i + 1}`),
  ...Array(30).fill(null).map((_, i) => `2nd Round, #${i + 31}`)
];

const priceRanges = [
  '0-50k€', '50-100k€', '100-150k€', '150-200k€', '200-250k€', '250-300k€', '300-350k€',
  '350-400k€', '400-450k€', '450-500k€', '500-550k€', '550-600k€', '600-650k€', '650-700k€',
  '700-750k€', '750-800k€', '800-850k€', '850-900k€', '900-950k€', '950k€-1M€', '1M€-1.2M€',
  '1.2M€-1.4M€', '1.4M€-1.6M€', '1.6M€-1.8M€', '1.8M€-2M€', '2M€-2.2M€', '2.2M€-2.4M€', '2.4M€-2.5M€'
];
const buyoutOptions = [...priceRanges];
const contratos = [
  '2-Way', 'Exhibit-10', '1 año', '1+1', '2 años', '3 años', 'On Loan'
];

const manoDominanteKeys = ['rightHand', 'leftHand', 'bothHands'];
const categoriaKeys = ['handler', 'exterior', 'interior'];
const shootingRangeOptions = ['Rim', 'Mid-Range', '3Pts'];
const flagBehaviourOptions = ['Green', 'Orange', 'Red'];
const injuryProneOptions = ['Green', 'Orange', 'Red'];
const booleanExperienceKeys = ['yes', 'no'];

const PlayerFiltersCaracterísticas = ({
  isMobile,

  rolOfensivoFilter, setRolOfensivoFilter,
  rolOfensivoCounts = {},
  manoDominanteFilter, setManoDominanteFilter,
  manoDominanteCounts = {},
  shootingRangeFilter, setShootingRangeFilter,
  shootingRangeCounts = {},
  rolDefensivoFilter, setRolDefensivoFilter,
  rolDefensivoCounts = {},
  categoriaFilter, setCategoriaFilter,
  categoriaCounts = {},
  jeJiTypeFilter, setJeJiTypeFilter,
  jeJiAttributesFilter, setJeJiAttributesFilter,
  currentVBCRatingFilter, setCurrentVBCRatingFilter,
  currentVBCRatingCounts = {},
  proyeccionVBCRatingFilter, setProyeccionVBCRatingFilter,
  proyeccionVBCRatingCounts = {},
  flagBehaviourFilter, setFlagBehaviourFilter,
  flagBehaviourCounts = {},
  injuryProneFilter, setInjuryProneFilter,
  injuryProneCounts = {},
  digitalBehaviourFilter, setDigitalBehaviourFilter,
  digitalBehaviourCounts = {},
  comparacionProFilter, setComparacionProFilter,

  nacionalidadFilter, setNacionalidadFilter,
  segundaNacionalidadFilter, setSegundaNacionalidadFilter,
  origenEtnicoFilter, setOrigenEtnicoFilter,
  origenEtnicoCounts = {},

  seleccionNacionalFilter, setSeleccionNacionalFilter,
  equipoActualFilter, setEquipoActualFilter,
  competicionFilter, setCompeticionFilter,
  competicionCounts = {},

  añoDraftFilter, setAñoDraftFilter,
  posicionDraftFilter, setPosicionDraftFilter,
  posicionDraftCounts = {},
  equipoPreDraftFilter, setEquipoPreDraftFilter,
  pitFilter, setPitFilter,
  eurocampFilter, setEurocampFilter,
  slvegasFilter, setSlvegasFilter,
  nbaExperienceFilter, setNbaExperienceFilter,
  nbaExperienceCounts = {},
  experienciaAcbFilter, setExperienciaAcbFilter,
  experienciaAcbCounts = {},
  experiencaEuropaFilter, setExperiencaEuropaFilter,
  experienciaEuropaCounts = {},
  experienca_fueraFilter, setExperienca_fueraFilter,
  experienciaFueraCounts = {},
  experienca_ncaaFilter, setExperienca_ncaaFilter,
  experienciaNcaaCounts = {},

  agenciaFilter, setAgenciaFilter,
  rangoPreciosFilter, setRangoPreciosFilter,
  rangoPreciosCounts = {},
  contratoFilter, setContratoFilter,
  contratoCounts = {},
  buyoutFilter, setBuyoutFilter,
  buyoutCounts = {},

  leagueFilter, setLeagueFilter,
  leagueCounts = {},
  competitionFilter, setCompetitionFilter,
  competitionCounts = {},
  licenciaFilter, setLicenciaFilter,
  licenciaCounts = {},
  uniqueLeagues,
  agencies,
  agenciaCounts = {},
  jeAttributeCounts = {},
  jiAttributeCounts = {}
}) => {
  const { t } = useTranslation();
  const { clubName } = useClubTheme();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // Créer les arrays traduits à partir des clés de traduction
  const offensiveRoles = offensiveRoleKeys.map(key => t(`createPlayer.offensiveRoles.${key}`));
  const defensiveRoles = defensiveRoleKeys.map(key => t(`createPlayer.defensiveRoles.${key}`));
  const JEAttributes = JEAttributeKeys.map(key => t(`createPlayer.jeAttributes.${key}`));
  const JIAttributes = JIAttributeKeys.map(key => t(`createPlayer.jiAttributes.${key}`));
  const vbcRatings = vbcRatingKeys.map(key => t(`createPlayer.levels.${key}`));
  const manoDominanteOptions = manoDominanteKeys.map(key => t(`createPlayer.fields.${key}`));
  const categoriaOptions = categoriaKeys.map(key => t(`createPlayer.categories.${key}`));
  const booleanExperienceOptions = booleanExperienceKeys.map(key => t(`common.${key}`));

  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [leagues2, setLeagues2] = useState([]);
  const [allCompetitions, setAllCompetitions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          resTeams,
          resLeagues2,
          resCompetitions,
          resEquipes
        ] = await Promise.all([
          api.get('/api/players/teams'),
          api.get('/api/players/leagues2'),
          api.get('/api/players/competitions/all'),
          api.get('/api/players/equipes/all')
        ]);

        setTeams(resTeams.data?.teams || []);
        setLeagues2(resLeagues2.data?.leagues2 || []);

        // Traiter UNIQUEMENT les équipes des nouvelles tables
        const { equipes = [] } = resEquipes.data || {};
        const teamsList = equipes.map(e => ({
          id: e.equipe_id,
          value: e.equipe_nom,
          nom: e.equipe_nom
        }));
        setAllTeams(teamsList);

        // Traiter UNIQUEMENT les compétitions des nouvelles tables
        const { competitions = [] } = resCompetitions.data || {};
        const competitionsList = competitions.map(c => ({
          id: c.competition_id,
          value: c.competition_nom,
          nom: c.competition_nom,
          niveau: c.niveau_nom
        }));
        setAllCompetitions(competitionsList);
      } catch (err) {
        console.error('Error fetching filter lists:', err);
      }
    };
    fetchData();
  }, [backendUrl, section]);

  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('caracteristicas');

  const renderGridItem = (content) => (
    <Grid item sx={{ flexBasis: '20%', maxWidth: '20%' }}>
      {content}
    </Grid>
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (isMobile) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
     

      <Collapse in={expanded}>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="none"
          sx={{ mb: 2 }}
        >
          <Tab
            value="caracteristicas"
            label={t('playerFiltersCaracteristicas.tabs.characteristics')}
            sx={{ '&.Mui-selected': { backgroundColor: 'orange', color: 'white' } }}
          />
          <Tab
            value="nacionalidades"
            label={t('playerFiltersCaracteristicas.tabs.nationalities')}
            sx={{ '&.Mui-selected': { backgroundColor: 'orange', color: 'white' } }}
          />
          <Tab
            value="situacion"
            label={t('playerFiltersCaracteristicas.tabs.currentSituation')}
            sx={{ '&.Mui-selected': { backgroundColor: 'orange', color: 'white' } }}
          />
          <Tab
            value="experiencia"
            label={t('playerFiltersCaracteristicas.tabs.experience')}
            sx={{ '&.Mui-selected': { backgroundColor: 'orange', color: 'white' } }}
          />
          <Tab
            value="contractual"
            label={t('playerFiltersCaracteristicas.tabs.contractual')}
            sx={{ '&.Mui-selected': { backgroundColor: 'orange', color: 'white' } }}
          />
        </Tabs>

        {activeTab === 'caracteristicas' && (
          <>
            <Grid container spacing={2}>
              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.offensiveRole')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(rolOfensivoFilter) ? rolOfensivoFilter : []}
                    onChange={(e) => setRolOfensivoFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {offensiveRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Checkbox checked={rolOfensivoFilter?.indexOf(role) > -1} />
                        <ListItemText primary={`${role} (${rolOfensivoCounts[role] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.defensiveRole')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(rolDefensivoFilter) ? rolDefensivoFilter : []}
                    onChange={(e) => setRolDefensivoFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {defensiveRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Checkbox checked={rolDefensivoFilter?.indexOf(role) > -1} />
                        <ListItemText primary={`${role} (${rolDefensivoCounts[role] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.dominantHand')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(manoDominanteFilter) ? manoDominanteFilter : []}
                    onChange={(e) => setManoDominanteFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {manoDominanteOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={manoDominanteFilter.indexOf(option) > -1} />
                        <ListItemText primary={`${option} (${manoDominanteCounts[option] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.category')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(categoriaFilter) ? categoriaFilter : []}
                    onChange={(e) => setCategoriaFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {categoriaOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={categoriaFilter.indexOf(option) > -1} />
                        <ListItemText primary={`${option} (${categoriaCounts[option] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.shootingRange')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(shootingRangeFilter) ? shootingRangeFilter : []}
                    onChange={(e) => setShootingRangeFilter(e.target.value)}
                    renderValue={(selected) => selected.map(opt => {
                      const shootingRangeMap = {
                        'Rim': t('createPlayer.fields.rim'),
                        'Mid-Range': t('createPlayer.fields.midRange'),
                        '3Pts': t('createPlayer.fields.threePoints')
                      };
                      return shootingRangeMap[opt] || opt;
                    }).join(', ')}
                  >
                    {shootingRangeOptions.map((option) => {
                      const shootingRangeMap = {
                        'Rim': t('createPlayer.fields.rim'),
                        'Mid-Range': t('createPlayer.fields.midRange'),
                        '3Pts': t('createPlayer.fields.threePoints')
                      };
                      return (
                        <MenuItem key={option} value={option}>
                          <Checkbox checked={shootingRangeFilter.indexOf(option) > -1} />
                          <ListItemText primary={`${shootingRangeMap[option] || option} (${shootingRangeCounts[option] || 0})`} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.skillsJeJi')}</InputLabel>
                  <Select
                    value={jeJiTypeFilter || ''}
                    onChange={(e) => {
                      setJeJiTypeFilter(e.target.value);
                      setJeJiAttributesFilter([]);
                    }}
                    label={t('playerFiltersCaracteristicas.labels.skillsJeJi')}
                  >
                    <MenuItem value="">{t('playerFiltersCaracteristicas.options.none')}</MenuItem>
                    <MenuItem value="JE">{t('playerFiltersCaracteristicas.options.jeExterior')}</MenuItem>
                    <MenuItem value="JI">{t('playerFiltersCaracteristicas.options.jiInterior')}</MenuItem>
                  </Select>
                </FormControl>
              )}

              {jeJiTypeFilter && renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>
                    {jeJiTypeFilter === 'JE' ? t('playerFiltersCaracteristicas.labels.skillsExterior') : t('playerFiltersCaracteristicas.labels.skillsInterior')}
                  </InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(jeJiAttributesFilter) ? jeJiAttributesFilter : []}
                    onChange={(e) => setJeJiAttributesFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {(jeJiTypeFilter === 'JE' ? JEAttributes : JIAttributes).map((attr) => (
                      <MenuItem key={attr} value={attr}>
                        <Checkbox checked={jeJiAttributesFilter?.indexOf(attr) > -1} />
                        <ListItemText primary={`${attr} (${jeJiTypeFilter === 'JE' ? (jeAttributeCounts[attr] || 0) : (jiAttributeCounts[attr] || 0)})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.currentVbcRating', { clubName })}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(currentVBCRatingFilter) ? currentVBCRatingFilter : []}
                    onChange={(e) => setCurrentVBCRatingFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {vbcRatingKeys.map((key) => {
                      const translatedRating = t(`createPlayer.levels.${key}`);
                      return (
                        <MenuItem key={key} value={translatedRating}>
                          <Checkbox checked={currentVBCRatingFilter.indexOf(translatedRating) > -1} />
                          <ListItemText primary={`${translatedRating} (${currentVBCRatingCounts[key] || currentVBCRatingCounts[translatedRating] || 0})`} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.projectionVbcRating', { clubName })}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(proyeccionVBCRatingFilter) ? proyeccionVBCRatingFilter : []}
                    onChange={(e) => setProyeccionVBCRatingFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {vbcRatingKeys.map((key) => {
                      const translatedRating = t(`createPlayer.levels.${key}`);
                      return (
                        <MenuItem key={key} value={translatedRating}>
                          <Checkbox checked={proyeccionVBCRatingFilter.indexOf(translatedRating) > -1} />
                          <ListItemText primary={`${translatedRating} (${proyeccionVBCRatingCounts[key] || proyeccionVBCRatingCounts[translatedRating] || 0})`} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.flagBehaviour')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(flagBehaviourFilter) ? flagBehaviourFilter : []}
                    onChange={(e) => setFlagBehaviourFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {flagBehaviourOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={flagBehaviourFilter.indexOf(option) > -1} />
                        <ListItemText primary={`${option} (${flagBehaviourCounts[option] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.injuryProne')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(injuryProneFilter) ? injuryProneFilter : []}
                    onChange={(e) => setInjuryProneFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {injuryProneOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={injuryProneFilter.indexOf(option) > -1} />
                        <ListItemText primary={`${option} (${injuryProneCounts[option] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.digitalBehaviour')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(digitalBehaviourFilter) ? digitalBehaviourFilter : []}
                    onChange={(e) => setDigitalBehaviourFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {injuryProneOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={digitalBehaviourFilter.indexOf(option) > -1} />
                        <ListItemText primary={`${option} (${digitalBehaviourCounts[option] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <TextField
                  fullWidth
                  label={t('playerFiltersCaracteristicas.labels.proComparison')}
                  value={comparacionProFilter || ''}
                  onChange={(e) => setComparacionProFilter(e.target.value)}
                />
              )}
            </Grid>
          </>
        )}

        {activeTab === 'nacionalidades' && (
          <>
            <Grid container spacing={2}>
              {renderGridItem(
                <NationalitiesSelect
                  value={nacionalidadFilter || ''}
                  onChange={(e) => setNacionalidadFilter(e.target.value)}
                />
              )}
              {renderGridItem(
                <NationalitiesSelect2
                  value={segundaNacionalidadFilter || ''}
                  onChange={(e) => setSegundaNacionalidadFilter(e.target.value)}
                />
              )}
              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.ethnicOrigin')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(origenEtnicoFilter) ? origenEtnicoFilter : []}
                    onChange={(e) => setOrigenEtnicoFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                    sx={{ whiteSpace: 'normal' }}
                  >
                    <MenuItem value="Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.') > -1}
                      />
                      <ListItemText primary={`Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc. (${origenEtnicoCounts['Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.') > -1}
                      />
                      <ListItemText primary={`Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc. (${origenEtnicoCounts['Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.') > -1}
                      />
                      <ListItemText primary={`Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc. (${origenEtnicoCounts['Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.') > -1}
                      />
                      <ListItemText primary={`Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc. (${origenEtnicoCounts['Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.') > -1}
                      />
                      <ListItemText primary={`Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc. (${origenEtnicoCounts['Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Nordafricanos - Tunez, Marruecos, Egipto, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Nordafricanos - Tunez, Marruecos, Egipto, etc.') > -1}
                      />
                      <ListItemText primary={`Nordafricanos - Tunez, Marruecos, Egipto, etc. (${origenEtnicoCounts['Nordafricanos - Tunez, Marruecos, Egipto, etc.'] || 0})`} />
                    </MenuItem>
                    <MenuItem value="Norteamericanos - Estados Unidos, Canada, etc.">
                      <Checkbox
                        checked={origenEtnicoFilter.indexOf('Norteamericanos - Estados Unidos, Canada, etc.') > -1}
                      />
                      <ListItemText primary={`Norteamericanos - Estados Unidos, Canada, etc. (${origenEtnicoCounts['Norteamericanos - Estados Unidos, Canada, etc.'] || 0})`} />
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
          </>
        )}

        {activeTab === 'situacion' && (
          <>
            <Grid container spacing={2}>
              {renderGridItem(
                <SelectSelectionNational
                  value={seleccionNacionalFilter || ''}
                  onChange={(e) => setSeleccionNacionalFilter(e.target.value)}
                />
              )}

              {renderGridItem(
                <Autocomplete
                  freeSolo
                  multiple
                  options={allTeams}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.nom}
                  value={Array.isArray(equipoActualFilter) ? equipoActualFilter : []}
                  onChange={(_, newValue) => {
                    // Si newValue contient des objets, extraire les valeurs
                    const values = newValue.map(v => typeof v === 'string' ? v : v.value);
                    setEquipoActualFilter(values);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('playerFiltersCaracteristicas.labels.teams')}
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => {
                    const mediaUrl = getMediaBaseUrl();
                    return (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="img"
                          src={`${mediaUrl}/equipes/${option.id}.jpg`}
                          alt={option.nom}
                          sx={{
                            width: 24,
                            height: 24,
                            objectFit: 'contain',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <span>{option.nom}</span>
                      </Box>
                    );
                  }}
                />
              )}


              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.competitions')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(competicionFilter) ? competicionFilter : []}
                    onChange={(e) => setCompeticionFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {allCompetitions.map((comp) => {
                      const mediaUrl = getMediaBaseUrl();
                      return (
                        <MenuItem key={comp.id} value={comp.value}>
                          <Checkbox checked={competicionFilter.indexOf(comp.value) > -1} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Box
                              component="img"
                              src={`${mediaUrl}/competitions/${comp.id}.jpg`}
                              alt={comp.nom}
                              sx={{
                                width: 24,
                                height: 24,
                                objectFit: 'contain',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <ListItemText
                              primary={comp.nom}
                              secondary={comp.niveau}
                              sx={{ margin: 0 }}
                            />
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.license')}</InputLabel>
                  <Select
                    multiple
                    value={licenciaFilter}
                    onChange={(e) => setLicenciaFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {['EXT', 'EUR', 'JFL', 'COT'].map((lic) => (
                      <MenuItem key={lic} value={lic}>
                        <Checkbox checked={licenciaFilter.indexOf(lic) > -1} />
                        <ListItemText primary={`${lic} (${licenciaCounts[lic] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            </Grid>
          </>
        )}

        {activeTab === 'experiencia' && (
          <>
            <Grid container spacing={2}>
              {renderGridItem(
                <TextField
                  fullWidth
                  label={t('playerFiltersCaracteristicas.labels.draftYear')}
                  type="number"
                  value={añoDraftFilter || ''}
                  onChange={(e) => setAñoDraftFilter(e.target.value)}
                />
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.draftPosition')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(posicionDraftFilter) ? posicionDraftFilter : []}
                    onChange={(e) => setPosicionDraftFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {draftPositions.map((dp) => (
                      <MenuItem key={dp} value={dp}>
                        <Checkbox checked={posicionDraftFilter.indexOf(dp) > -1} />
                        <ListItemText primary={`${dp} (${posicionDraftCounts[dp] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <TextField
                  fullWidth
                  label={t('playerFiltersCaracteristicas.labels.preDraftTeam')}
                  value={equipoPreDraftFilter || ''}
                  onChange={(e) => setEquipoPreDraftFilter(e.target.value)}
                />
              )}
              {renderGridItem(
                <TextField
                  fullWidth
                  label="PIT"
                  type="number"
                  value={pitFilter || ''}
                  onChange={(e) => setPitFilter(e.target.value)}
                />
              )}
              {renderGridItem(
                <TextField
                  fullWidth
                  label="Eurocamp"
                  type="number"
                  value={eurocampFilter || ''}
                  onChange={(e) => setEurocampFilter(e.target.value)}
                />
              )}
              {renderGridItem(
                <TextField
                  fullWidth
                  label="SL Vegas"
                  type="number"
                  value={slvegasFilter || ''}
                  onChange={(e) => setSlvegasFilter(e.target.value)}
                />
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.nbaExperience')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(nbaExperienceFilter) ? nbaExperienceFilter : []}
                    onChange={(e) => setNbaExperienceFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {booleanExperienceOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={nbaExperienceFilter.indexOf(opt) > -1} />
                        <ListItemText primary={`${opt} (${nbaExperienceCounts[opt] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.acbExperience')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(experienciaAcbFilter) ? experienciaAcbFilter : []}
                    onChange={(e) => setExperienciaAcbFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {booleanExperienceOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={experienciaAcbFilter.indexOf(opt) > -1} />
                        <ListItemText primary={`${opt} (${experienciaAcbCounts[opt] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.europeExperience')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(experiencaEuropaFilter) ? experiencaEuropaFilter : []}
                    onChange={(e) => setExperiencaEuropaFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {booleanExperienceOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={experiencaEuropaFilter.indexOf(opt) > -1} />
                        <ListItemText primary={`${opt} (${experienciaEuropaCounts[opt] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.outsideExperience')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(experienca_fueraFilter) ? experienca_fueraFilter : []}
                    onChange={(e) => setExperienca_fueraFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {booleanExperienceOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={experienca_fueraFilter.indexOf(opt) > -1} />
                        <ListItemText primary={`${opt} (${experienciaFueraCounts[opt] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.ncaaExperience')}</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(experienca_ncaaFilter) ? experienca_ncaaFilter : []}
                    onChange={(e) => setExperienca_ncaaFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {booleanExperienceOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={experienca_ncaaFilter.indexOf(opt) > -1} />
                        <ListItemText primary={`${opt} (${experienciaNcaaCounts[opt] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </>
        )}

        {activeTab === 'contractual' && (
          <>
            <Grid container spacing={2}>
              {renderGridItem(
                <FormControl fullWidth>
                  <InputLabel>{t('playerFiltersCaracteristicas.labels.agency')}</InputLabel>
                  <Select
                    multiple
                    value={agenciaFilter}
                    onChange={(e) => setAgenciaFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {agencies.map((agency) => (
                      <MenuItem key={agency} value={agency}>
                        <Checkbox checked={agenciaFilter.indexOf(agency) > -1} />
                        <ListItemText primary={`${agency} (${agenciaCounts[agency] || 0})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {renderGridItem(
                <Autocomplete
                  multiple
                  options={priceRanges}
                  value={Array.isArray(rangoPreciosFilter) ? rangoPreciosFilter : []}
                  onChange={(_, newVal) => setRangoPreciosFilter(newVal)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option} ({rangoPreciosCounts[option] || 0})
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('playerFiltersCaracteristicas.labels.salaryRange')}
                      fullWidth
                    />
                  )}
                />
              )}

              {renderGridItem(
                <Autocomplete
                  multiple
                  options={contratos}
                  value={Array.isArray(contratoFilter) ? contratoFilter : []}
                  onChange={(_, newVal) => setContratoFilter(newVal)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option} ({contratoCounts[option] || 0})
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('playerFiltersCaracteristicas.labels.contract')}
                      fullWidth
                    />
                  )}
                />
              )}

              {renderGridItem(
                <Autocomplete
                  multiple
                  options={buyoutOptions}
                  value={Array.isArray(buyoutFilter) ? buyoutFilter : []}
                  onChange={(_, newVal) => setBuyoutFilter(newVal)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option} ({buyoutCounts[option] || 0})
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('playerFiltersCaracteristicas.labels.buyout')}
                      fullWidth
                    />
                  )}
                />
              )}
            </Grid>
          </>
        )}
      </Collapse>
    </Box>
  );
};

export default PlayerFiltersCaracterísticas;